import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import {
  beginPendingCheckoutRestore,
  consumePendingCheckoutRestore,
  clearSavedCheckoutReduxCartState,
  getSavedCheckoutReduxCartState,
  getOrCreateCheckoutObservabilityAttempt,
} from 'storage/checkout'
import { replaceWithRegion } from 'actions/NavigationActions'
import { restoreCheckoutCart } from 'actions/CheckoutActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { trackClientEvent } from 'analytics/analytics'
import uuidV4 from 'lib/string/uuidV4Utils'
import deserializeCartState from 'checkout/lib/utils/deserializeCartState'
import { STEP_BEDBANK_CHANGE_DATES } from 'constants/booking'
import { checkoutSessionContext } from 'analytics/snowplow/eventContexts/checkoutSessionContext'
import { pruneIncompleteBundleItems } from 'lib/checkout/checkoutUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reportLog } from 'services/userMonitoring'

export function withoutOrphanedBundledExperienceItems(
  cartState: Partial<App.CheckoutCartState>,
): Partial<App.CheckoutCartState> {
  if (!cartState.items?.length) return cartState
  return { ...cartState, items: pruneIncompleteBundleItems(cartState.items) }
}

const PAYMENT_RETURN_QUERY_PARAMS = ['payment_intent', 'sessionId', 'redirect_status'] as const

function isPaymentRedirectReturn(search?: string): boolean {
  const params = new URLSearchParams(search ?? '')
  return PAYMENT_RETURN_QUERY_PARAMS.some(param => params.has(param))
}

/**
 * Seat checkout details are not persisted. Restoring that cart after a refresh
 * can POST addons again while the flight item is still `processing_addon`.
 * Payment-redirect returns still need the cart so checkout can finalise.
 */
export function getAbandonedFlightSeatsRestorePath(
  cartState: { mode?: App.CheckoutCartMode, existingOrder?: { id?: string } } | undefined,
  search?: string,
): string | undefined {
  if (cartState?.mode !== 'flight-seats') return undefined
  if (isPaymentRedirectReturn(search)) return undefined
  return cartState.existingOrder?.id
    ? `/account/my-escapes/order/${cartState.existingOrder.id}`
    : '/account/my-escapes'
}

export function useRestoreCartState(cartId?: string) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const isCartRestored = useAppSelector(state => state.checkout.isCartRestored)
  const isAbandonedCart = useAppSelector(state => state.checkout.cart.isAbandonedCart)
  const cartTransactionKey = useAppSelector(state => state.checkout.cart.transactionKey)
  const cartItemCount = useAppSelector(state => state.checkout.cart.items.length)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const pathname = useAppSelector(state => state.router.location.pathname)
  const search = useAppSelector(state => state.router.location.search)
  const isCheckoutPage = pathname.includes('checkout')
  // Bedbank change-dates builds its own cart; persisted restore can race it.
  const isBedbankChangeDatesPage = pathname.includes(STEP_BEDBANK_CHANGE_DATES)

  useEffect(() => {
    if (!isCheckoutPage) return
    if (isBedbankChangeDatesPage) return
    if (isCartRestored && !isAbandonedCart) return

    const { state: cartState, timestamp } = getSavedCheckoutReduxCartState() ?? {}
    if (!cartState && !cartId) return

    const abandonedFlightSeatsPath = getAbandonedFlightSeatsRestorePath(cartState, search)
    if (abandonedFlightSeatsPath) {
      reportLog('flight_seats_checkout_abandoned_on_refresh', {
        orderId: cartState?.existingOrder?.id,
        transactionKey: cartState?.transactionKey,
      }, 'warn')
      clearSavedCheckoutReduxCartState()
      dispatch(restoreCheckoutCart({}))
      showSnackbar(intl.formatMessage({
        id: 'J3NoX3VvmQ2UWD8rkl5O',
        defaultMessage: 'Your seat update was interrupted. Please try again from your booking.',
        description: 'Snackbar shown after a page refresh abandons an in-progress post-booking seat checkout',
      }), 'critical')
      dispatch(replaceWithRegion(abandonedFlightSeatsPath))
      return
    }

    const persistedTransactionKey = cartState?.transactionKey

    // If a cartId was passed to this function and it is different than the one in memory
    // Use it and restore that cart
    if (cartId && persistedTransactionKey !== cartId) {
      trackClientEvent({
        subject: 'checkout_url',
        action: 'opened',
        category: 'logging',
        type: 'operational',
      })
      dispatch(replaceWithRegion(`/checkout-restore-cart?cartId=${cartId}`))
      return
    }

    // Do not overwrite a freshly initialised in-memory cart with stale persisted data.
    if (
      cartItemCount > 0 &&
      persistedTransactionKey &&
      cartTransactionKey &&
      persistedTransactionKey !== cartTransactionKey
    ) {
      return
    }

    const deserializedCartState = cartState ? deserializeCartState(cartState) : {}
    const isOlderThan30Min = !timestamp || Date.now() - timestamp > 30 * 60 * 1000
    const restoredCartTransactionKey = persistedTransactionKey && isOlderThan30Min ? uuidV4() : undefined
    if (persistedTransactionKey && restoredCartTransactionKey) {
      beginPendingCheckoutRestore(persistedTransactionKey, restoredCartTransactionKey)
    }
    dispatch(restoreCheckoutCart(withoutOrphanedBundledExperienceItems({
      ...deserializedCartState,
      ...(restoredCartTransactionKey && { transactionKey: restoredCartTransactionKey }),
    })))
  }, [dispatch, intl, isCartRestored, isAbandonedCart, isCheckoutPage, isBedbankChangeDatesPage, cartId, cartTransactionKey, cartItemCount, search])

  useEffect(() => {
    if (!isCheckoutPage || isBedbankChangeDatesPage || !isCartRestored || !cartTransactionKey) return

    const pendingRestore = consumePendingCheckoutRestore(cartTransactionKey)
    const { attempt, isNew } = getOrCreateCheckoutObservabilityAttempt(cartTransactionKey, pendingRestore)
    if (!isNew) return

    trackClientEvent({
      category: 'checkout',
      subject: 'attempt',
      action: 'entered',
      type: 'operational',
      context: [checkoutSessionContext({
        checkoutSessionId: attempt.checkoutAttemptId,
        checkoutLineageId: attempt.checkoutLineageId,
      })],
    })

    if (pendingRestore) {
      trackClientEvent({
        category: 'checkout',
        subject: 'attempt',
        action: 'restore_linked',
        type: 'operational',
        context: [checkoutSessionContext({
          checkoutSessionId: attempt.checkoutAttemptId,
          checkoutLineageId: attempt.checkoutLineageId,
          sourceCartId: pendingRestore.sourceCartId,
          restoredFromCheckoutSessionId: pendingRestore.sourceCheckoutAttemptId,
        })],
      })
    }
  }, [cartTransactionKey, isBedbankChangeDatesPage, isCartRestored, isCheckoutPage])

  useEffect(() => {
    if (!isCheckoutPage && isSpoofed) {
      const cartState = getSavedCheckoutReduxCartState()?.state
      if (!cartState) return
      if (getAbandonedFlightSeatsRestorePath(cartState)) {
        clearSavedCheckoutReduxCartState()
        return
      }
      const deserializedState = deserializeCartState(cartState)
      dispatch(restoreCheckoutCart(withoutOrphanedBundledExperienceItems(deserializedState)))
    }
  }, [dispatch, isCheckoutPage, isSpoofed])
}
